import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';
import type { ExamName } from '../examTasks/examTaskTypes';

export type StudyPlanSkill = 'Speaking' | 'Writing' | 'Reading' | 'Listening';
export type StudyPlanProfileStatus = 'Draft' | 'Active' | 'Archived';

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    statusCode?: number;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

export type AdminStudyPlanProfile = {
    id: string;
    studyPlanAlgorithmProfileId: string;
    profileName: string;
    versionNumber: number;
    status: StudyPlanProfileStatus | string;
    isDefault: boolean;
    description?: string | null;
    createdByUserId?: string | null;
    publishedByUserId?: string | null;
    publishedAt?: string | null;
    createdAt: string;
    updatedAt: string;
};

export type AdminStudyPlanProfileListResponse = {
    page: number;
    pageSize: number;
    totalCount: number;
    items: AdminStudyPlanProfile[];
};

export type AdminStudyPlanSkillMarkRangeRule = {
    studyPlanSkillMarkRangeRuleId?: string | null;
    studyPlanAlgorithmProfileId: string;
    examKind: ExamName;
    skill: StudyPlanSkill;
    rangeKey: string;
    displayName: string;
    minScore: number;
    maxScore: number;
    rangeWeight: number;
    displayOrder: number;
    description?: string | null;
};

export type AdminStudyPlanTaskRule = {
    studyPlanTaskRuleId?: string | null;
    studyPlanAlgorithmProfileId: string;
    examKind: ExamName;
    examTaskType: string;
    displayName: string;
    estimatedMinutesPerItem: number;
    priorityBand: string;
    basePriorityWeight: number;
    prioritySortOrder: number;
    compressionBehavior: string;
    completionRequiresScored: boolean;
    allowMockItemCredit: boolean;
    countDistinctExamTaskOnly: boolean;
    retryCreditPolicy: string;
    isRequiredInPlan: boolean;
    settingsJson?: Record<string, unknown> | null;
};

export type AdminStudyPlanTaskDailyRequirementRule = {
    studyPlanTaskDailyRequirementRuleId?: string | null;
    studyPlanAlgorithmProfileId: string;
    studyPlanTaskRuleId: string;
    studyPlanSkillMarkRangeRuleId: string;
    examKind: ExamName;
    examTaskType: string;
    driverSkill: StudyPlanSkill;
    rangeKey: string;
    minDailyCount: number;
    recommendedDailyCount: number;
    maxNormalDailyCount?: number | null;
    weeklyMinDays?: number | null;
    combineStrategy: string;
    isEnabled: boolean;
};

export type AdminStudyPlanTaskSkillWeight = {
    studyPlanTaskSkillWeightId?: string | null;
    studyPlanTaskRuleId: string;
    skill: StudyPlanSkill;
    weight: number;
};

export type AdminStudyPlanMultiplierRule = {
    studyPlanMultiplierRuleId?: string | null;
    studyPlanAlgorithmProfileId: string;
    ruleType: string;
    ruleKey: string;
    minValue?: number | null;
    maxValue?: number | null;
    multiplier: number;
    settingsJson?: Record<string, unknown> | null;
    displayOrder: number;
};

export type AdminStudyPlanMockRule = {
    studyPlanMockRuleId?: string | null;
    studyPlanAlgorithmProfileId: string;
    ruleScope: string;
    examKind: ExamName;
    skill?: StudyPlanSkill | null;
    studyPlanSkillMarkRangeRuleId?: string | null;
    rangeKey?: string | null;
    minDaysUntilExam: number;
    maxDaysUntilExam: number;
    fullMockMinimumCount: number;
    fullMockMaximumCount?: number | null;
    sectionMockMinimumCount: number;
    preferredFirstMockDayOffset?: number | null;
    preferredFinalMockDaysBeforeExam?: number | null;
    fullMockReviewMinutes: number;
    sectionMockReviewMinutes: number;
    combineStrategy: string;
    countDistinctMockTestOnly: boolean;
    retakeCreditPolicy: string;
    settingsJson?: Record<string, unknown> | null;
};

export type AdminStudyPlanTaskScoreThresholdRule = {
    studyPlanTaskScoreThresholdRuleId?: string | null;
    studyPlanAlgorithmProfileId: string;
    studyPlanTaskRuleId: string;
    studyPlanSkillMarkRangeRuleId?: string | null;
    examKind: ExamName;
    examTaskType: string;
    skill: StudyPlanSkill;
    rangeKey?: string | null;
    requiredScoreOutOf90: number;
    strongScoreOutOf90?: number | null;
    minimumConsistencyPercent: number;
    minimumRecentDistinctItems: number;
    rollingDayWindow: number;
    rollingItemWindow: number;
    scorePickStrategy: string;
    dimensionWeightsJson?: Record<string, unknown> | null;
    isEnabled: boolean;
};

export type AdminStudyPlanReadinessWeightRule = {
    studyPlanReadinessWeightRuleId?: string | null;
    studyPlanAlgorithmProfileId: string;
    ruleKey: string;
    scope: string;
    examKind?: ExamName | null;
    skill?: StudyPlanSkill | null;
    studyPlanTaskRuleId?: string | null;
    studyPlanSkillMarkRangeRuleId?: string | null;
    effortWeight: number;
    outputWeight: number;
    mockWeight: number;
    trendWeight: number;
    stabilityWeight: number;
    recencyWeight: number;
    minimumEvidenceCount: number;
    mockFreshnessDays?: number | null;
    settingsJson?: Record<string, unknown> | null;
    isEnabled: boolean;
};

export type AdminStudyPlanReadinessStatusRule = {
    studyPlanReadinessStatusRuleId?: string | null;
    studyPlanAlgorithmProfileId: string;
    scope: string;
    statusKey: string;
    minReadinessPercent?: number | null;
    maxReadinessPercent?: number | null;
    minEffortPercent?: number | null;
    minOutputPercent?: number | null;
    maxScoreGapPoints?: number | null;
    requiresRecentMock: boolean;
    confidenceFloor?: string | null;
    severity: string;
    messageTemplate?: string | null;
    displayOrder: number;
    isEnabled: boolean;
};

export type AdminStudyPlanProfileDetail = {
    profile: AdminStudyPlanProfile;
    skillMarkRanges: AdminStudyPlanSkillMarkRangeRule[];
    taskRules: AdminStudyPlanTaskRule[];
    taskDailyRequirements: AdminStudyPlanTaskDailyRequirementRule[];
    taskSkillWeights: AdminStudyPlanTaskSkillWeight[];
    multiplierRules: AdminStudyPlanMultiplierRule[];
    mockRules: AdminStudyPlanMockRule[];
    taskScoreThresholds: AdminStudyPlanTaskScoreThresholdRule[];
    readinessWeights: AdminStudyPlanReadinessWeightRule[];
    readinessStatuses: AdminStudyPlanReadinessStatusRule[];
};

export type AdminStudyPlanValidation = {
    studyPlanAlgorithmProfileId: string;
    isValid: boolean;
    errors: string[];
    warnings: string[];
    counts: Record<string, number>;
};

export type AdminStudyPlanConfigCollection =
    | 'skillMarkRanges'
    | 'taskRules'
    | 'taskDailyRequirements'
    | 'taskSkillWeights'
    | 'multiplierRules'
    | 'mockRules'
    | 'taskScoreThresholds'
    | 'readinessWeights'
    | 'readinessStatuses';

type AdminStudyPlanCollectionPayload = {
    skillMarkRanges: AdminStudyPlanSkillMarkRangeRule[];
    taskRules: AdminStudyPlanTaskRule[];
    taskDailyRequirements: AdminStudyPlanTaskDailyRequirementRule[];
    taskSkillWeights: AdminStudyPlanTaskSkillWeight[];
    multiplierRules: AdminStudyPlanMultiplierRule[];
    mockRules: AdminStudyPlanMockRule[];
    taskScoreThresholds: AdminStudyPlanTaskScoreThresholdRule[];
    readinessWeights: AdminStudyPlanReadinessWeightRule[];
    readinessStatuses: AdminStudyPlanReadinessStatusRule[];
};

const collectionPaths: Record<AdminStudyPlanConfigCollection, string> = {
    skillMarkRanges: 'skill-mark-ranges',
    taskRules: 'task-rules',
    taskDailyRequirements: 'task-daily-requirements',
    taskSkillWeights: 'task-skill-weights',
    multiplierRules: 'multiplier-rules',
    mockRules: 'mock-rules',
    taskScoreThresholds: 'task-score-thresholds',
    readinessWeights: 'readiness-weights',
    readinessStatuses: 'readiness-status-rules',
};

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function trimSlashes(value: string): string {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    return { baseUrl, apiVersion, adminToken };
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct = candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }
    return fallback;
}

function unwrapAdminApiPayload<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) {
        return payload as T;
    }

    const envelope = payload as AdminApiEnvelope<T>;
    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Admin study plan request failed'));
    }

    return envelope.data !== undefined ? envelope.data : (payload as T);
}

async function requestAdminStudyPlan<T>(
    path: string,
    init: RequestInit = {},
    query?: Record<string, string>,
): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const resourcePath = trimSlashes(path);
    const url = new URL(
        resourcePath
            ? `${apiRoot}/AdminStudyPlanAlgorithmProfiles/${resourcePath}`
            : `${apiRoot}/AdminStudyPlanAlgorithmProfiles`,
    );

    for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== '') {
            url.searchParams.set(key, value);
        }
    }

    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) headers.set('Accept', 'application/json');
    if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (adminToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), {
        ...init,
        headers,
    });

    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
        const envelope = payload as AdminApiEnvelope<unknown>;
        if (envelope.success === false) {
            throw new Error(getErrorMessage(envelope, 'Admin study plan request failed'));
        }
    }

    return payload as T;
}

export async function getAdminStudyPlanProfiles(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: string;
}): Promise<AdminStudyPlanProfileListResponse> {
    const response = await requestAdminStudyPlan<unknown>('', { method: 'GET' }, {
        page: String(params?.page ?? 1),
        pageSize: String(params?.pageSize ?? 25),
        search: params?.search?.trim() ?? '',
        status: params?.status ?? '',
    });

    return unwrapAdminApiPayload(response as AdminStudyPlanProfileListResponse | AdminApiEnvelope<AdminStudyPlanProfileListResponse>);
}

export async function getAdminStudyPlanProfile(profileId: string): Promise<AdminStudyPlanProfileDetail> {
    const response = await requestAdminStudyPlan<unknown>(profileId, { method: 'GET' });
    return unwrapAdminApiPayload(response as AdminStudyPlanProfileDetail | AdminApiEnvelope<AdminStudyPlanProfileDetail>);
}

export async function createAdminStudyPlanProfile(input: {
    profileName: string;
    description?: string;
}): Promise<AdminStudyPlanProfile> {
    const response = await requestAdminStudyPlan<unknown>('', {
        method: 'POST',
        body: JSON.stringify(input),
    });
    return unwrapAdminApiPayload(response as AdminStudyPlanProfile | AdminApiEnvelope<AdminStudyPlanProfile>);
}

export async function cloneAdminStudyPlanProfile(profileId: string): Promise<AdminStudyPlanProfile> {
    const response = await requestAdminStudyPlan<unknown>(`${profileId}/clone`, { method: 'POST' });
    return unwrapAdminApiPayload(response as AdminStudyPlanProfile | AdminApiEnvelope<AdminStudyPlanProfile>);
}

export async function validateAdminStudyPlanProfile(profileId: string): Promise<AdminStudyPlanValidation> {
    const response = await requestAdminStudyPlan<unknown>(`${profileId}/validate`, { method: 'POST' });
    return unwrapAdminApiPayload(response as AdminStudyPlanValidation | AdminApiEnvelope<AdminStudyPlanValidation>);
}

export async function publishAdminStudyPlanProfile(profileId: string): Promise<AdminStudyPlanProfile> {
    const response = await requestAdminStudyPlan<unknown>(`${profileId}/publish`, { method: 'POST' });
    return unwrapAdminApiPayload(response as AdminStudyPlanProfile | AdminApiEnvelope<AdminStudyPlanProfile>);
}

export async function archiveAdminStudyPlanProfile(profileId: string): Promise<void> {
    await requestAdminStudyPlan<unknown>(`${profileId}/archive`, { method: 'POST' });
}

export async function upsertAdminStudyPlanCollection<K extends AdminStudyPlanConfigCollection>(
    profileId: string,
    collection: K,
    items: AdminStudyPlanCollectionPayload[K],
): Promise<AdminStudyPlanProfileDetail> {
    const response = await requestAdminStudyPlan<unknown>(`${profileId}/${collectionPaths[collection]}`, {
        method: 'PUT',
        body: JSON.stringify(items),
    });

    return unwrapAdminApiPayload(response as AdminStudyPlanProfileDetail | AdminApiEnvelope<AdminStudyPlanProfileDetail>);
}
